import type {
  AnnouncementCreatePayload,
  AnnouncementPriority,
} from "@/modules/announcements/domain/types";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import {
  FormDateTimePicker,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeftIcon,
  BellIcon,
  ImageIcon,
  InfoIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  ANNOUNCEMENT_IMAGE_KEY_PREFIX,
  AnnouncementImageUploadField,
} from "./AnnouncementImageUploadField";
import {
  AnnouncementBodyEditor,
  isAnnouncementBodyEmpty,
} from "./AnnouncementBodyEditor";

const PRIORITY_OPTIONS: { value: AnnouncementPriority; label: string }[] = [
  { value: "NORMAL", label: "ທົ່ວໄປ" },
  { value: "HIGH", label: "ສຳຄັນ" },
  { value: "URGENT", label: "ດ່ວນ" },
];

const AnnouncementFormSchema = z
  .object({
    title: z.string().min(2, "ກະລຸນາໃສ່ຫົວຂໍ້"),
    summary: z.string().nullable().optional(),
    body: z
      .string()
      .refine((v) => !isAnnouncementBodyEmpty(v), "ກະລຸນາໃສ່ເນື້ອຫາ"),
    imageUrl: z.string().nullable().optional(),
    priority: z.enum(["NORMAL", "HIGH", "URGENT"]),
    showOnOpen: z.boolean(),
    sendPush: z.boolean(),
    publishAt: z.string().min(1, "ກະລຸນາໃສ່ວັນເລີ່ມເຜີຍແຜ່"),
    expiresAt: z.string().nullable().optional(),
    isActive: z.boolean(),
  })
  .refine(
    (d) => {
      if (!d.expiresAt) return true;
      return new Date(d.publishAt) < new Date(d.expiresAt);
    },
    {
      message: "ວັນເລີ່ມເຜີຍແຜ່ຕ້ອງກ່ອນວັນໝົດອາຍຸ",
      path: ["expiresAt"],
    },
  );

export type AnnouncementFormValues = z.infer<typeof AnnouncementFormSchema>;

type AnnouncementFormProps = {
  defaultValues?: Partial<AnnouncementFormValues>;
  syncKey?: string;
  onSubmit: (values: AnnouncementCreatePayload) => void | Promise<void>;
  submitting?: boolean;
  onCancel: () => void;
  submitLabel: string;
};

function buildDefaultValues(
  partial?: Partial<AnnouncementFormValues>,
): AnnouncementFormValues {
  return {
    title: "",
    summary: null,
    body: "",
    imageUrl: null,
    priority: "NORMAL",
    showOnOpen: false,
    sendPush: false,
    publishAt: "",
    expiresAt: null,
    isActive: true,
    ...partial,
  };
}

export function AnnouncementForm({
  defaultValues,
  syncKey,
  onSubmit,
  submitting,
  onCancel,
  submitLabel,
}: AnnouncementFormProps) {
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const methods = useForm<AnnouncementFormValues>({
    resolver: zodResolver(AnnouncementFormSchema),
    defaultValues: buildDefaultValues(defaultValues),
  });

  useEffect(() => {
    if (!syncKey) return;
    methods.reset(buildDefaultValues(defaultValues));
    setPendingImageFile(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync เมื่อ server data เปลี่ยน
  }, [syncKey]);

  const handleSubmit = async (values: AnnouncementFormValues) => {
    let imageUrl = values.imageUrl?.trim() || null;
    if (pendingImageFile) {
      imageUrl = await uploadImageToKey(
        ANNOUNCEMENT_IMAGE_KEY_PREFIX,
        pendingImageFile,
      );
      methods.setValue("imageUrl", imageUrl);
      setPendingImageFile(null);
    }

    await onSubmit({
      title: values.title.trim(),
      summary: values.summary?.trim() || null,
      body: values.body.trim(),
      imageUrl,
      priority: values.priority,
      showOnOpen: values.showOnOpen,
      sendPush: values.sendPush,
      publishAt: values.publishAt,
      expiresAt: values.expiresAt || null,
      isActive: values.isActive,
    });
  };

  return (
    <FormRoot<AnnouncementFormValues> methods={methods} onSubmit={handleSubmit}>
      <div className="space-y-6">
        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ຮູບຂ່າວສານ</h3>
            </div>
            <p className="text-muted-foreground text-sm">
              ທາງເລືອກ — ອັດຕາສ່ວນ 4:3 ໃນແອັບລູກຄ້າ
            </p>
            <RHF.Controller
              name="imageUrl"
              control={methods.control}
              render={({ field }) => (
                <AnnouncementImageUploadField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  pendingFile={pendingImageFile}
                  onFileSelect={setPendingImageFile}
                  disabled={submitting}
                />
              )}
            />
          </CardContent>
        </Card>

        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <InfoIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ຂໍ້ມູນຂ່າວສານ</h3>
            </div>
            <FormInput
              name="title"
              label="ຫົວຂໍ້"
              placeholder="ຕົວຢ່າງ: ປິດປັບປຸງລະບົບ"
              requiredMark
            />
            <FormTextarea
              name="summary"
              label="ສະຫຼຸບ"
              placeholder="ຂໍ້ຄວາມສັ້ນ (ທາງເລືອກ)"
              rows={2}
            />
            <RHF.Controller
              name="body"
              control={methods.control}
              render={({ field, fieldState }) => (
                <div>
                  <AnnouncementBodyEditor
                    value={field.value}
                    onChange={field.onChange}
                    disabled={submitting}
                  />
                  {fieldState.error ? (
                    <p className="text-destructive mt-1 text-xs">
                      {fieldState.error.message}
                    </p>
                  ) : null}
                </div>
              )}
            />
            <FormSelect
              name="priority"
              label="ລຳດັບຄວາມສຳຄັນ"
              options={PRIORITY_OPTIONS}
              placeholder="ເລືອກລຳດັບ"
            />
          </CardContent>
        </Card>

        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <BellIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ການເຜີຍແຜ່</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormDateTimePicker
                name="publishAt"
                label="ວັນເລີ່ມເຜີຍແຜ່"
                requiredMark
                captionLayout="dropdown"
                fromYear={new Date().getFullYear() - 1}
                toYear={new Date().getFullYear() + 3}
              />
              <FormDateTimePicker
                name="expiresAt"
                label="ວັນໝົດອາຍຸ (ທາງເລືອກ)"
                clearable
                captionLayout="dropdown"
                fromYear={new Date().getFullYear() - 1}
                toYear={new Date().getFullYear() + 5}
              />
            </div>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">ເຜີຍແຜ່</span>
                <RHF.Controller
                  name="isActive"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  ແຈ້ງເຕືອນຕອນເປີດແອັບ
                </span>
                <RHF.Controller
                  name="showOnOpen"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">ສົ່ງ push (FCM)</span>
                <RHF.Controller
                  name="sendPush"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Push ແຍກຈາກ popup — ສົ່ງໄປມືຖືເມື່ອເຜີຍແຜ່ (ຫຼືເມື່ອຮອດວັນເຜີຍແຜ່)
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeftIcon className="size-4" />
          ຍົກເລີກ
        </Button>
      </div>
    </FormRoot>
  );
}
